import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Clock, ArrowLeft, Mail, CheckCircle, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [verified, setVerified] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === 5) {
      const fullCode = newCode.join("");
      if (fullCode.length === 6) {
        handleVerify(fullCode);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData) {
      const newCode = pastedData.split("");
      while (newCode.length < 6) newCode.push("");
      setCode(newCode);
      
      if (pastedData.length === 6) {
        handleVerify(pastedData);
      }
    }
  };

  const handleVerify = async (codeStr = null) => {
    const verifyCode = codeStr || code.join("");
    
    if (!email) {
      toast.error("メールアドレスを入力してください");
      return;
    }
    
    if (verifyCode.length !== 6) {
      toast.error("6桁のコードを入力してください");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-email`, {
        email,
        code: verifyCode
      });
      
      setVerified(true);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "認証に失敗しました");
      // Clear code on error
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      toast.error("メールアドレスを入力してください");
      return;
    }

    setResending(true);
    try {
      const res = await axios.post(`${API}/auth/resend-code`, { email });
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "再送信に失敗しました");
    } finally {
      setResending(false);
    }
  };

  if (verified) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">認証完了</h2>
            <p className="text-slate-500 mb-6">
              メールアドレスが認証されました。<br />
              ログインしてご利用ください。
            </p>
            <Button 
              onClick={() => navigate("/login")}
              className="w-full btn-primary"
              data-testid="go-to-login-btn"
            >
              ログインする
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white mb-8"
          data-testid="back-to-login"
        >
          <ArrowLeft className="w-4 h-4" />
          ログインに戻る
        </Link>

        <Card>
          <CardHeader className="text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <Mail className="w-7 h-7 text-slate-600 dark:text-slate-400" />
            </div>
            <CardTitle className="text-2xl">メールアドレス認証</CardTitle>
            <CardDescription>
              {email ? (
                <>
                  <span className="font-medium text-slate-900 dark:text-white">{email}</span>
                  <br />に送信された6桁のコードを入力してください
                </>
              ) : (
                "メールアドレスと認証コードを入力してください"
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Email input (if not provided) */}
            {!searchParams.get("email") && (
              <div className="space-y-2">
                <Label htmlFor="email">メールアドレス</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@email.com"
                  data-testid="verify-email-input"
                />
              </div>
            )}

            {/* 6-digit code input */}
            <div className="space-y-2">
              <Label>認証コード</Label>
              <div className="flex gap-2 justify-center" onPaste={handlePaste}>
                {code.map((digit, index) => (
                  <Input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-14 text-center text-2xl font-bold"
                    data-testid={`code-input-${index}`}
                  />
                ))}
              </div>
            </div>

            <Button
              onClick={() => handleVerify()}
              disabled={loading || code.join("").length !== 6}
              className="w-full btn-primary"
              data-testid="verify-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  認証中...
                </>
              ) : (
                "認証する"
              )}
            </Button>

            <div className="text-center">
              <p className="text-sm text-slate-500 mb-2">
                コードが届かない場合
              </p>
              <Button
                variant="ghost"
                onClick={handleResend}
                disabled={resending}
                className="text-sm"
                data-testid="resend-code-btn"
              >
                {resending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    送信中...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    コードを再送信
                  </>
                )}
              </Button>
            </div>

            <div className="text-center text-xs text-slate-400">
              認証コードの有効期限は24時間です
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
